<template>
  <Layout>
    <div class="py-12 px-4 sm:px-6 lg:px-8">
      <div class="max-w-3xl mx-auto">
      <!-- Header -->
      <div class="text-center mb-8">
        <div v-if="company.logoUrl" class="mb-4">
          <img :src="company.logoUrl" :alt="company.name" class="h-16 mx-auto" />
        </div>
        <h1 class="text-4xl font-bold text-gray-900 mb-2">
          {{ company.name || $t('home.title') }}
        </h1>
        <p class="text-lg text-gray-600">
          {{ $t('home.subtitle') }}
        </p>
      </div>

      <!-- Upload Card -->
      <div class="card p-8">
        <div class="text-center mb-6">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 mb-4">
            <svg class="h-6 w-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </div>
          <h2 class="text-2xl font-semibold text-gray-900 mb-2">
            {{ $t('home.uploadTitle') }}
          </h2>
          <p class="text-gray-600">
            {{ $t('home.uploadSubtitle') }}
          </p>
        </div>

        <!-- Upload Area -->
        <div
          @drop.prevent="handleDrop"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @click="triggerFileInput"
          :class="[
            'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
            isDragging ? 'border-primary-400 bg-primary-50' : 'border-gray-300 hover:border-primary-400'
          ]"
        >
          <input
            ref="fileInput"
            type="file"
            @change="handleFileSelect"
            class="hidden"
            accept="*/*"
          />
          
          <div v-if="!selectedFile" class="space-y-4">
            <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
              <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <div>
              <p class="text-lg text-gray-600">
                <span class="font-medium text-primary-600">{{ $t('home.clickToUpload') }}</span>
                {{ $t('home.dragAndDrop') }}
              </p>
              <p class="text-sm text-gray-500 mt-1">
                {{ $t('home.maxSize') }}: {{ formatFileSize(company.uploadLimit || 1000000000) }}
              </p>
            </div>
          </div>

          <div v-else class="space-y-4">
            <div class="flex items-center justify-center">
              <svg class="h-12 w-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p class="text-lg font-medium text-gray-900">{{ selectedFile.name }}</p>
              <p class="text-sm text-gray-500">{{ formatFileSize(selectedFile.size) }}</p>
            </div>
            <button
              @click.stop="removeFile"
              class="text-sm text-red-600 hover:text-red-800"
            >
              {{ $t('home.removeFile') }}
            </button>
          </div>
        </div>

        <!-- Form Fields -->
        <div v-if="selectedFile" class="mt-6 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              {{ $t('home.recipientEmail') }}
            </label>
            <input
              v-model="recipientEmail"
              type="email"
              :placeholder="$t('home.recipientEmailPlaceholder')"
              class="input-field"
              required
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Votre nom (optionnel)
            </label>
            <input
              v-model="uploadedBy"
              type="text"
              placeholder="Votre nom"
              class="input-field"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              {{ $t('home.message') }}
            </label>
            <textarea
              v-model="message"
              rows="3"
              :placeholder="$t('home.messagePlaceholder')"
              class="input-field"
            ></textarea>
          </div>

          <!-- Upload Button -->
          <button
            @click="uploadFile"
            :disabled="isUploading || !recipientEmail"
            class="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="isUploading" class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ $t('home.uploading') }}
            </span>
            <span v-else>
              {{ $t('home.uploadButton') }}
            </span>
          </button>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p class="text-red-800">{{ error }}</p>
        </div>
      </div>

      <!-- Footer -->
      <div class="text-center mt-8 text-sm text-gray-500">
        <p>Vos fichiers sont sécurisés et expireront automatiquement</p>
      </div>
    </div>
  </div>
  </Layout>
</template>

<script>
import apiClient from '../config/axios'
import Layout from './Layout.vue'

export default {
  name: 'UploadPage',
  components: {
    Layout
  },
  data() {
    return {
      company: {},
      selectedFile: null,
      recipientEmail: '',
      uploadedBy: '',
      message: '',
      isDragging: false,
      isUploading: false,
      error: null
    }
  },
  async mounted() {
    await this.loadCompanyInfo()
  },
  methods: {
    async loadCompanyInfo() {
      try {
        const subdomain = this.getSubdomain()
        const response = await apiClient.get(`/companies/${subdomain}`)
        this.company = response.data
      } catch (error) {
        console.error('Error loading company info:', error)
        // Use default values
        this.company = {
          name: 'ProTransfert',
          uploadLimit: 1000000000
        }
      }
    },
    getSubdomain() {
      const hostname = window.location.hostname
      const parts = hostname.split('.')
      return parts.length > 2 ? parts[0] : 'default'
    },
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    handleFileSelect(event) {
      const file = event.target.files[0]
      if (file) {
        this.validateAndSetFile(file)
      }
    },
    handleDrop(event) {
      this.isDragging = false
      const file = event.dataTransfer.files[0]
      if (file) {
        this.validateAndSetFile(file)
      }
    },
    validateAndSetFile(file) {
      const maxSize = this.company.uploadLimit || 1000000000
      
      if (file.size > maxSize) {
        this.error = `Le fichier est trop volumineux. Taille maximale: ${this.formatFileSize(maxSize)}`
        return
      }
      
      this.selectedFile = file
      this.error = null
    },
    removeFile() {
      this.selectedFile = null
      this.$refs.fileInput.value = ''
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    async uploadFile() {
      if (!this.selectedFile || !this.recipientEmail) {
        this.error = 'Veuillez sélectionner un fichier et saisir l\'email du destinataire'
        return
      }

      console.log('🚀 [FRONTEND] Début de l\'upload');
      console.log('📁 [FRONTEND] Fichier sélectionné:');
      console.log('   - Nom:', `"${this.selectedFile.name}"`);
      console.log('   - Taille:', this.selectedFile.size);
      console.log('   - Type:', this.selectedFile.type);
      console.log('   - Email destinataire:', this.recipientEmail);

      this.isUploading = true
      this.error = null

      try {
        const formData = new FormData()
        formData.append('file', this.selectedFile)
        formData.append('recipientEmail', this.recipientEmail)
        formData.append('uploadedBy', this.uploadedBy)
        formData.append('companySubdomain', this.getSubdomain())

        console.log('📤 [FRONTEND] Envoi de la requête...');

        const response = await apiClient.post('/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        console.log('✅ [FRONTEND] Réponse reçue:');
        console.log('   - Nom dans la réponse:', `"${response.data.fileName}"`);
        console.log('   - ID du fichier:', response.data.fileId);
        console.log('   - URL de téléchargement:', response.data.downloadUrl);

        // Store upload data for confirmation page
        localStorage.setItem('uploadData', JSON.stringify({
          ...response.data,
          message: this.message
        }))

        console.log('💾 [FRONTEND] Données sauvegardées dans localStorage');

        // Redirect to confirmation page
        this.$router.push('/confirm')

      } catch (error) {
        console.error('Upload error:', error)
        this.error = error.response?.data?.error || 'Erreur lors de l\'upload du fichier'
      } finally {
        this.isUploading = false
      }
    }
  }
}
</script> 